import { createSlice } from "@reduxjs/toolkit";

/**
 * Redux slice for managing master data (Subjects, Classes, Chapters)
 * This slice handles fetching and storing hierarchical master data
 * from the backend APIs.
 * 
 * API Endpoints:
 * - GET /api/subjects - Fetch all subjects
 * - GET /api/classes - Fetch all classes
 * - GET /api/chapters - Fetch all chapters
 */

const initialState = {
  subjects: {
    data: [],
    loading: false,
    error: null,
  },
  classes: {
    data: [],
    loading: false,
    error: null,
  },
  chapters: {
    data: [],
    loading: false,
    error: null,
  },
  categories: {
    data: [],
    loading: false,
    error: null,
  },
};

const masterDataSlice = createSlice({
  name: "masterData",
  initialState,
  reducers: {
    // ==================== SUBJECTS ====================
    /**
     * Action dispatched when fetching subjects starts
     */
    fetchSubjectsRequest: (state) => {
      state.subjects.loading = true;
      state.subjects.error = null;
    },
    /**
     * Action dispatched when subjects are successfully fetched
     * @param {Array} action.payload - Array of subject objects
     */
    fetchSubjectsSuccess: (state, action) => {
      state.subjects.loading = false;
      state.subjects.data = action.payload;
      state.subjects.error = null;
    },
    /**
     * Action dispatched when fetching subjects fails
     * @param {string} action.payload - Error message
     */
    fetchSubjectsFailure: (state, action) => {
      state.subjects.loading = false;
      state.subjects.error = action.payload;
    },

    // ==================== CLASSES ====================
    /**
     * Action dispatched when fetching classes starts
     */
    fetchClassesRequest: (state) => {
      state.classes.loading = true;
      state.classes.error = null;
    },
    /**
     * Action dispatched when classes are successfully fetched
     * @param {Array} action.payload - Array of class objects
     */
    fetchClassesSuccess: (state, action) => {
      state.classes.loading = false;
      state.classes.data = action.payload;
      state.classes.error = null;
    },
    /**
     * Action dispatched when fetching classes fails
     * @param {string} action.payload - Error message
     */
    fetchClassesFailure: (state, action) => {
      state.classes.loading = false;
      state.classes.error = action.payload;
    },

    // ==================== CHAPTERS ====================
    /**
     * Action dispatched when fetching chapters starts
     */
    fetchChaptersRequest: (state) => {
      state.chapters.loading = true;
      state.chapters.error = null;
    },
    /**
     * Action dispatched when chapters are successfully fetched
     * @param {Array} action.payload - Array of chapter objects
     */
    fetchChaptersSuccess: (state, action) => {
      state.chapters.loading = false;
      state.chapters.data = action.payload;
      state.chapters.error = null;
    },
    /**
     * Action dispatched when fetching chapters fails
     * @param {string} action.payload - Error message
     */
    fetchChaptersFailure: (state, action) => {
      state.chapters.loading = false;
      state.chapters.error = action.payload;
    },

    // ==================== CATEGORIES ====================
    /**
     * Action dispatched when fetching categories starts
     */
    fetchCategoriesRequest: (state) => {
      state.categories.loading = true;
      state.categories.error = null;
    },
    /**
     * Action dispatched when categories are successfully fetched
     * @param {Array} action.payload - Array of category objects
     */
    fetchCategoriesSuccess: (state, action) => {
      state.categories.loading = false;
      state.categories.data = action.payload;
      state.categories.error = null;
    },
    /**
     * Action dispatched when fetching categories fails
     * @param {string} action.payload - Error message
     */
    fetchCategoriesFailure: (state, action) => {
      state.categories.loading = false;
      state.categories.error = action.payload;
    },

    // ==================== UTILITY ACTIONS ====================
    /**
     * Reset all master data to initial state
     */
    resetMasterData: (state) => {
      state.subjects = initialState.subjects;
      state.classes = initialState.classes;
      state.chapters = initialState.chapters;
      state.categories = initialState.categories;
    },
    /**
     * Clear errors for all entities
     */
    clearErrors: (state) => {
      state.subjects.error = null;
      state.classes.error = null;
      state.chapters.error = null;
      state.categories.error = null;
    },
  },
});

// Export actions
export const {
  // Subjects
  fetchSubjectsRequest,
  fetchSubjectsSuccess,
  fetchSubjectsFailure,
  // Classes
  fetchClassesRequest,
  fetchClassesSuccess,
  fetchClassesFailure,
  // Chapters
  fetchChaptersRequest,
  fetchChaptersSuccess,
  fetchChaptersFailure,
  // Categories
  fetchCategoriesRequest,
  fetchCategoriesSuccess,
  fetchCategoriesFailure,
  // Utility
  resetMasterData,
  clearErrors,
} = masterDataSlice.actions;

// Export selectors for easy state access
export const selectSubjects = (state) => state.masterData?.subjects || initialState.subjects;
export const selectClasses = (state) => state.masterData?.classes || initialState.classes;
export const selectChapters = (state) => state.masterData?.chapters || initialState.chapters;
export const selectCategories = (state) => state.masterData?.categories || initialState.categories;

// Selector to check if any master data is loading
export const selectIsAnyLoading = (state) => {
  const masterData = state.masterData;
  if (!masterData) return false;
  return masterData.subjects.loading || masterData.classes.loading || masterData.chapters.loading || masterData.categories.loading;
};

// Selector to get all errors
export const selectAllErrors = (state) => {
  const masterData = state.masterData;
  if (!masterData) return [];
  return [
    masterData.subjects.error,
    masterData.classes.error,
    masterData.chapters.error,
    masterData.categories.error,
  ].filter(Boolean);
};

export default masterDataSlice.reducer;
